import { PAGES } from "@dune/types";
import { Button, Card, cn, H2, H3, Separator } from "@dune/ui";
import { getMOAFromConstructionSiteDetails } from "@dune/utils";
import { useQueryClient } from "@tanstack/react-query";
import { Pen } from "lucide-react";
import { useMemo, useState } from "react";
import { FormProvider } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import {
	useUnlinkFileFromConstructionSiteSubmission,
	useUpdateConstructionSiteSubmissionOperation,
} from "../../../api/mutations/constructionSiteSubmission.mutations";
import { useImportEmailAttachmentsConstructionSite } from "../../../api/mutations/mail.mutations";
import { useGetConstructionSiteSubmissionOperation } from "../../../api/queries/constructionSiteSubmission.queries";
import { constructionSiteSubmissionQueryKeys } from "../../../api/query-keys/constructionSiteSubmission.query-keys";
import DatesDetailsContainer from "../../../component/brokers/constructionSites/DatesDetailsContainer";
import { useConstructionSiteDetailsForm } from "../../../component/brokers/constructionSites/useConstructionSiteDetailsForm";
import { OperationDocuments } from "../../../component/submission-operation/OperationDocumentsTable";
import { AdministrativeAuthorizationForm } from "../../../component/submission-operation/administrativeAuthorizationForm/AdministrativeAuthorizationForm";
import { useAdministrativeAuthorizationForm } from "../../../component/submission-operation/administrativeAuthorizationForm/useAdministrativeAuthorizationForm";
import { MoaForm } from "../../../component/submission-operation/moaForm/MoaForm";
import { useMoaForm } from "../../../component/submission-operation/moaForm/useMoaForm";
import { OperationDescriptionForm } from "../../../component/submission-operation/operationDescriptionForm/OperationDescriptionForm";
import { useOperationDescriptionForm } from "../../../component/submission-operation/operationDescriptionForm/useOperationDescriptionForm";
import { useEntityPanelSync } from "../../../hooks/useEntityPanelSync";
import { useRequiredParams } from "../../../hooks/useRequiredParams";
import { ConstructionSiteEmailComposerPanel } from "../components/panels/ConstructionSiteEmailComposerPanel";
import ModifyInformationModal from "../components/modals/ModifyInformationModal";
import ConstructionSiteTaskDetailsPanel from "../components/panels/ConstructionSiteTaskDetailsPanel";
import { MainDetailsOperation } from "../components/tabs/operation";
import { generateBrokerEmailWithFallback } from "../../../utils/emails.utils";
import OperationDescriptiveBlock from "../../../component/brokers/constructionSites/OperationDescriptiveBlock";
import InterventionsDetailsContainer from "../../../component/brokers/constructionSites/InterventionsDetailsContainer";
import DeclarationsContainer from "../../../component/brokers/constructionSites/DeclarationsContainer";

interface SubmissionOperationProps {
	handlePreviousStep?: () => void;
	handleNextStep?: () => void;
	hideTitle?: boolean;
	disabledInputs?: boolean;
	disabledDocuments?: boolean;
}

export const SubmissionOperation = ({
	handlePreviousStep,
	handleNextStep,
	hideTitle = false,
	disabledInputs = false,
	disabledDocuments = false,
}: SubmissionOperationProps) => {
	const { constructionSiteId } = useRequiredParams<{
		constructionSiteId: string;
	}>();

	const navigate = useNavigate();
	const [isModifyModalOpen, setIsModifyModalOpen] = useState(false);

	const {
		data: submissionWithOperationDetails,
		isLoading,
		error,
	} = useGetConstructionSiteSubmissionOperation(Number(constructionSiteId));

	const { form: constructionSiteDetailsForm } = useConstructionSiteDetailsForm(
		submissionWithOperationDetails?.constructionSite,
	);

	const { form: administrativeAuthorizationForm } =
		useAdministrativeAuthorizationForm(submissionWithOperationDetails);

	const { form: operationDescriptionForm } = useOperationDescriptionForm(
		submissionWithOperationDetails,
	);

	const {
		mutate: updateAdministrativeAuthorization,
		isPending: isUpdatingAdministrativeAuthorization,
	} = useUpdateConstructionSiteSubmissionOperation(Number(constructionSiteId));

	const handleSaveAdministrativeAuthorization = (onSuccess?: () => void) => {
		administrativeAuthorizationForm.handleSubmit((adminAuthData) => {
			operationDescriptionForm.handleSubmit((operationDescData) => {
				updateAdministrativeAuthorization(
					{
						administrativeAuthorizationType:
							adminAuthData.administrativeAuthorizationType,
						authorizationReference: adminAuthData.authorizationReference,
						coOwnersSyndicDenomination:
							operationDescData.coOwnersSyndicDenomination,
						operationDescription: operationDescData.operationDescription,
					},
					{
						onSuccess: onSuccess,
					},
				);
			})();
		})();
	};

	const isSyndicProject = useMemo(() => {
		const constructionSite = submissionWithOperationDetails?.constructionSite;
		const details = constructionSite?.constructionSiteDetails;
		const action = details?.constructionSiteAction;

		if (!details) return false;

		const buildingType = action?.buildingType;
		return buildingType === "PRIVATE_WORK" || buildingType === "COMMON_PARTS";
	}, [submissionWithOperationDetails]);

	const constructionSiteClientDetails =
		submissionWithOperationDetails?.constructionSite
			.constructionSiteClientDetails;

	const moaToUse = useMemo(
		() => getMOAFromConstructionSiteDetails(constructionSiteClientDetails),
		[constructionSiteClientDetails],
	);

	const moaInitialValues = useMemo(() => {
		if (moaToUse) {
			if ("siren" in moaToUse) {
				return {
					siren: moaToUse?.siren ?? "",
					denomination: moaToUse?.denomination ?? "",
					streetNumber: moaToUse?.headOfficeAddress?.streetNumber ?? "",
					streetName: moaToUse?.headOfficeAddress?.streetName ?? "",
					postalCode: moaToUse?.headOfficeAddress?.postalCode ?? "",
					city: moaToUse?.headOfficeAddress?.city ?? "",
				};
			}

			return {
				moaUsers: moaToUse.map((user) => ({
					moaLastname: user?.lastname ?? "",
					moaFirstname: user?.firstname ?? "",
					streetNumber: user?.address?.streetNumber ?? "",
					streetName: user?.address?.streetName ?? "",
					postalCode: user?.address?.postalCode ?? "",
					city: user?.address?.city ?? "",
				})),
			};
		}
	}, [moaToUse]);

	const { form: moaForm } = useMoaForm(moaInitialValues);
	const queryClient = useQueryClient();

	const { mutate: importEmailAttachments } =
		useImportEmailAttachmentsConstructionSite(Number(constructionSiteId));

	const { mutate: unlinkFile } = useUnlinkFileFromConstructionSiteSubmission(
		Number(constructionSiteId),
	);

	const panelComponents = useMemo(
		() => ({
			TaskDetails: ConstructionSiteTaskDetailsPanel,
			EmailComposer: ConstructionSiteEmailComposerPanel,
		}),
		[],
	);

	const panelEmailConfig = useMemo(
		() => ({
			getReplyToEmail: (entityId: number, emailRoutingUuid?: string | null) => {
				if (emailRoutingUuid) {
					return generateBrokerEmailWithFallback(emailRoutingUuid, entityId);
				}
				return `chantier-${entityId}@dune.com`;
			},
			importEmailAttachmentsHandler: (emailAttachmentId: number) => {
				importEmailAttachments({ emailAttachmentId });
			},
		}),
		[importEmailAttachments],
	);

	const documentConfig = useMemo(
		() => ({
			onDelete: (documentId: string) => {
				unlinkFile(documentId);
			},
		}),
		[unlinkFile],
	);

	useEntityPanelSync({
		entityType: "CONSTRUCTION_SITE",
		entityId: Number(constructionSiteId),
		communications: [],
		tasks: [],
		emailRoutingUuid: null,
		components: panelComponents,
		emailConfig: panelEmailConfig,
		documentConfig: documentConfig,
	});

	const handleModifyConfirm = () => {
		navigate(PAGES.PROJECTS.edit.withParams(constructionSiteId));
	};

	const handleModifyCancel = () => {
		setIsModifyModalOpen(false);
	};

	if (isLoading) return <div>Chargement des détails de l'opération...</div>;
	if (error)
		return <div>Erreur lors du chargement des détails de l'opération.</div>;

	if (!submissionWithOperationDetails)
		return <div>Aucune opération trouvée</div>;

	return (
		<div>
			{!hideTitle && <H2>Opération</H2>}

			<FormProvider {...moaForm}>
				<div className="flex flex-row gap-4">
					<div className="flex-1">
						<div className="flex flex-row justify-between items-center">
							<H3 className={cn("", !disabledInputs && "mb-0")}>
								MAITRISE D'OUVRAGE
							</H3>
							{!disabledInputs && (
								<Button
									variant="ghostBlue"
									onClick={() => setIsModifyModalOpen(true)}
								>
									<Pen />
									Modifier
								</Button>
							)}
						</div>
						<Card noShadow className="p-6">
							<MoaForm
								disabled
								isSociety={moaToUse ? "siren" in moaToUse : false}
							/>
						</Card>
					</div>
					<div className="flex-1">
						<OperationDocuments
							constructionSiteId={Number(constructionSiteId)}
							section="MOA"
							submissionOperationDetails={submissionWithOperationDetails}
							disabled={disabledDocuments}
						/>
					</div>
				</div>
			</FormProvider>

			<Separator className="my-8" />

			<FormProvider {...constructionSiteDetailsForm}>
				<div className="flex flex-row gap-4">
					<div className="flex-1">
						<div className="flex flex-row justify-between items-center">
							<H3 className={cn("", !disabledInputs && "mb-0")}>
								INFORMATIONS SUR L'OPÉRATION
							</H3>
							{!disabledInputs && (
								<Button
									variant="ghostBlue"
									onClick={() => setIsModifyModalOpen(true)}
								>
									<Pen />
									Modifier
								</Button>
							)}
						</div>
						<OperationDescriptiveBlock className="mb-4" hideTitle />
						<MainDetailsOperation isDisabled noShadow showConstructionCost />
						<div className="my-4" />
						<InterventionsDetailsContainer isDisabled hideUncheckedOptions />
						<div className="my-4" />
						<DeclarationsContainer isDisabled />
					</div>
					<div className="flex-1">
						<OperationDocuments
							constructionSiteId={Number(constructionSiteId)}
							section="OPERATION_INFORMATION"
							submissionOperationDetails={submissionWithOperationDetails}
							disabled={disabledDocuments}
						/>
					</div>
				</div>
			</FormProvider>

			<Separator className="my-8" />

			<FormProvider {...operationDescriptionForm}>
				<div className="flex flex-row gap-4">
					<div className="flex-1">
						<H3>DESCRIPTION DE L'OPÉRATION</H3>
						<Card noShadow className="p-6">
							<OperationDescriptionForm
								disabled={disabledInputs}
								showCoOwnersSyndicDenomination={isSyndicProject}
							/>
						</Card>
					</div>
				</div>
			</FormProvider>

			<Separator className="my-8" />

			<FormProvider {...administrativeAuthorizationForm}>
				<div className="flex flex-row gap-4">
					<div className="flex-1">
						<H3>AUTORISATION ADMINISTRATIVE</H3>
						<Card noShadow className="p-6">
							<AdministrativeAuthorizationForm disabled={disabledInputs} />
						</Card>
					</div>
					<div className="flex-1">
						<OperationDocuments
							constructionSiteId={Number(constructionSiteId)}
							section="ADMINISTRATIVE_AUTHORIZATION"
							submissionOperationDetails={submissionWithOperationDetails}
							disabled={disabledDocuments}
						/>
					</div>
				</div>
			</FormProvider>

			<Separator className="my-8" />

			<FormProvider {...constructionSiteDetailsForm}>
				<div className="flex flex-row gap-4">
					<div className="flex-1">
						<div className="flex flex-row justify-between items-center">
							<H3 className={cn("", !disabledInputs && "mb-0")}>
								DATES DE L'OPÉRATION
							</H3>
							{!disabledInputs && (
								<Button
									variant="ghostBlue"
									onClick={() => setIsModifyModalOpen(true)}
								>
									<Pen />
									Modifier
								</Button>
							)}
						</div>
						<DatesDetailsContainer noShadow isDisabled />
					</div>
					<div className="flex-1">
						<OperationDocuments
							constructionSiteId={Number(constructionSiteId)}
							section="OPERATION_DATES"
							submissionOperationDetails={submissionWithOperationDetails}
							disabled={disabledDocuments}
						/>
					</div>
				</div>
			</FormProvider>

			{handlePreviousStep && handleNextStep && (
				<div className="flex justify-between mt-8">
					<Button variant="outline" onClick={handlePreviousStep}>
						Précédent
					</Button>
					<div className="flex gap-2">
						{!disabledInputs && (
							<Button
								variant="outline"
								disabled={isUpdatingAdministrativeAuthorization}
								isLoading={isUpdatingAdministrativeAuthorization}
								onClick={() => {
									handleSaveAdministrativeAuthorization(() => {
										navigate(-1);
									});
								}}
							>
								Enregistrer et fermer
							</Button>
						)}
						<Button
							disabled={isUpdatingAdministrativeAuthorization}
							isLoading={isUpdatingAdministrativeAuthorization}
							onClick={() => {
								if (disabledInputs) {
									handleNextStep();
								} else {
									handleSaveAdministrativeAuthorization(() => {
										queryClient.invalidateQueries({
											queryKey: constructionSiteSubmissionQueryKeys.operation(
												Number(constructionSiteId),
											),
										});
										handleNextStep();
									});
								}
							}}
						>
							Suivant
						</Button>
					</div>
				</div>
			)}

			<ModifyInformationModal
				open={isModifyModalOpen}
				onOpenChange={setIsModifyModalOpen}
				onConfirm={handleModifyConfirm}
				onCancel={handleModifyCancel}
			/>
		</div>
	);
};
